import type PropertyHeroSection from '~/components/PropertyHeroSection/HeroSection.vue';
import type { ComputedRef, Ref } from 'vue';

import { SECTION_TYPES } from '~/components/ContextualHeader';

import { useEvents } from './events/useEvents';

interface UsePropertyContextualMenuOptions {
	shortsSectionRef?: Ref<HTMLElement | null>;
	hasShorts?: Ref<boolean> | ComputedRef<boolean>;
	hasChosenForYou?: Ref<boolean> | ComputedRef<boolean>;
}

export const usePropertyContextualMenu = (
	options: UsePropertyContextualMenuOptions = {}
) => {
	const contactRef = useTemplateRef<HTMLElement>('contactRef');
	const locationRef = useTemplateRef<HTMLElement>('locationRef');
	const overviewRef = useTemplateRef<HTMLElement>('overviewRef');
	const chosenForYouRef = useTemplateRef<HTMLElement>('chosenForYouRef');
	const heroSectionRef =
		useTemplateRef<InstanceType<typeof PropertyHeroSection>>('heroSectionRef');
	const characteristicsRef = useTemplateRef<HTMLElement>('characteristicsRef');
	const propertyPricesRef = useTemplateRef<HTMLElement>('propertyPricesRef');
	const highlightedRegionPropertiesDetailsSectionRef =
		useTemplateRef<HTMLElement>('highlightedRegionPropertiesDetailsSectionRef');
	const shortsSectionRef = computed(
		() => options.shortsSectionRef?.value || null
	);
	const hasShorts = computed(() => options.hasShorts?.value || false);
	const hasChosenForYou = computed(
		() => options.hasChosenForYou?.value ?? Boolean(chosenForYouRef.value)
	);

	const heroSectionEl = computed(() => heroSectionRef.value?.$el);

	const similarPropertiesEl = computed<HTMLElement | null>(
		() =>
			(hasChosenForYou.value ? chosenForYouRef.value : null) ||
			highlightedRegionPropertiesDetailsSectionRef.value
	);

	const sectionRefs = computed(() => {
		const baseRefs = [characteristicsRef, locationRef, contactRef];

		if (hasShorts.value && shortsSectionRef.value) {
			baseRefs.push(shortsSectionRef);
		}

		if (similarPropertiesEl.value) {
			baseRefs.push(similarPropertiesEl);
		}

		return [overviewRef, ...baseRefs];
	});

	useContextualHeaderVisibilityControl({
		heroSectionRef: heroSectionEl,
		sectionRefs,
		anchorSection: similarPropertiesEl
	});

	const { trackMenuPropertyClicked } = useEvents();

	const tabClickedCleanup = onClientEventBus(
		useEventBus<string>('tab-clicked'),
		tabId => {
			trackMenuPropertyClicked({
				click_attributes: tabId.includes(SECTION_TYPES.CHARACTERISTICS),
				click_location: tabId.includes(SECTION_TYPES.LOCATION),
				click_overview: tabId.includes(SECTION_TYPES.OVERVIEW),
				click_service: tabId.includes(SECTION_TYPES.CONTACT),
				click_shorts: tabId.includes(SECTION_TYPES.SHORTS),
				click_similar_properties:
					tabId.includes(SECTION_TYPES.SIMILAR_PROPERTIES) ||
					tabId.includes(SECTION_TYPES.CHOSEN_FOR_YOU)
			});
		}
	);

	onUnmounted(() => {
		tabClickedCleanup();
	});

	return {
		contactRef,
		locationRef,
		overviewRef,
		chosenForYouRef,
		heroSectionRef,
		characteristicsRef,
		propertyPricesRef
	};
};
